import { NON_SELF_CULLING, OPAQUE_BLOCKS, TRANSLUCENT_BLOCKS } from './BlockFlags.js'
import { Identifier } from './core/Identifier.js'
import type { BlockFlagsProvider, BlockPropertiesProvider } from './StructureRenderer.js'
import { BlockDefinition } from './render/BlockDefinition.js'
import { BlockModel } from './render/BlockModel.js'
import { TextureAtlas } from './render/TextureAtlas.js'
import type { TextureAtlasProvider } from './render/TextureAtlas.js'
import { upperPowerOfTwo } from './math/Util.js'
import type { BlockDefinitionProvider } from './render/BlockDefinition.js'
import type { BlockModelProvider } from './render/BlockModel.js'

export type BrowserAtlasSource = HTMLImageElement | HTMLCanvasElement | ImageBitmap | ImageData

export interface BrowserResourceAssets {
	blocks: Record<string, any>
	blockstates: Record<string, any>
	models: Record<string, any>
	uvMapping: Record<string, [number, number, number, number]>
	atlas: BrowserAtlasSource
}

export class BrowserResourceManager implements BlockDefinitionProvider, BlockModelProvider, BlockFlagsProvider, BlockPropertiesProvider, TextureAtlasProvider {
	private blockDefinitions: { [id: string]: BlockDefinition } = {}
	private blockModels: { [id: string]: BlockModel } = {}
	private textureAtlas: TextureAtlas
	private readonly blocks: Map<string, {
		default: Record<string, string>,
		properties: Record<string, string[]>,
	}>
	private blockNonTransparent: { [id: string]: boolean } = {}
	private blockIsCube: { [id: string]: boolean } = {}
	private mipMapLevelFour: Uint8Array | undefined

	constructor(assets: BrowserResourceAssets) {
		this.blocks = new Map(Object.entries(assets.blocks)
			.map(([k, v]: [string, any]) => [
				Identifier.create(k).toString(),
				{ properties: v[0], default: v[1] },
			]))
		this.textureAtlas = this.createTextureAtlas(assets.uvMapping, assets.atlas)
		this.loadBlockDefinitions(assets.blockstates)
		this.loadBlockModels(assets.models)
	}

	public getBlockDefinition(id: Identifier) {
		return this.blockDefinitions[id.toString()]
	}

	public getBlockModel(id: Identifier) {
		return this.blockModels[id.toString()]
	}

	public getTextureUV(id: Identifier) {
		return this.textureAtlas.getTextureUV(id)
	}

	public getTextureAtlas() {
		return this.textureAtlas.getTextureAtlas()
	}

	public getPixelSize(): number {
		return this.textureAtlas.getPixelSize()
	}

	public getBlockFlags(id: Identifier) {
		const str = id.toString()
		return {
			opaque: OPAQUE_BLOCKS.has(str),
			semi_transparent: TRANSLUCENT_BLOCKS.has(str),
			self_culling: !NON_SELF_CULLING.has(str),
		}
	}

	public getBlockProperties(id: Identifier) {
		return this.blocks.get(id.toString())?.properties ?? null
	}

	public getDefaultBlockProperties(id: Identifier) {
		return this.blocks.get(id.toString())?.default ?? null
	}

	public getBlockIsOpaque(blockId: Identifier, gl: WebGL2RenderingContext): boolean {
		return this.getBlockIsCube(blockId) && this.getBlockIsNonTransparent(blockId, gl)
	}

	public getBlockIsCube(blockId: Identifier): boolean {
		const str = blockId.toString()
		if (this.blockIsCube[str] !== undefined) {
			return this.blockIsCube[str]
		}
		const blockDefinition = this.getBlockDefinition(blockId)
		if (blockDefinition === undefined) {
			this.blockIsCube[str] = false
			return false
		}
		for (const modelId of blockDefinition.getAllModelIds()) {
			const blockModel = this.getBlockModel(modelId)
			if (!blockModel?.isCube()) {
				this.blockIsCube[str] = false
				return false
			}
		}
		this.blockIsCube[str] = true
		return true
	}

	public getBlockIsNonTransparent(blockId: Identifier, gl: WebGL2RenderingContext): boolean {
		const str = blockId.toString()
		if (this.blockNonTransparent[str] !== undefined) {
			return this.blockNonTransparent[str]
		}
		const imageData = this.textureAtlas.getTextureAtlas()
		if (this.mipMapLevelFour === undefined) {
			this.mipMapLevelFour = this.getMipMapLevels(imageData, gl, [4])[0]
		}
		const blockDefinition = this.getBlockDefinition(blockId)
		if (blockDefinition === undefined) {
			this.blockNonTransparent[str] = false
			return false
		}
		for (const modelId of blockDefinition.getAllModelIds()) {
			const blockModel = this.getBlockModel(modelId)
			if (!blockModel) {
				this.blockNonTransparent[str] = false
				return false
			}
			for (const textureId of blockModel.getAllTextureIds()) {
				if (textureId.namespace !== 'minecraft' || !this.getTextureIsOpaque(textureId, imageData, this.mipMapLevelFour)) {
					this.blockNonTransparent[str] = false
					return false
				}
			}
		}
		this.blockNonTransparent[str] = true
		return true
	}

	private loadBlockDefinitions(definitions: Record<string, any>) {
		Object.keys(definitions).forEach(id => {
			this.blockDefinitions[Identifier.create(id).toString()] = BlockDefinition.fromJson(definitions[id])
		})
	}

	private loadBlockModels(models: Record<string, any>) {
		Object.keys(models).forEach(id => {
			this.blockModels[Identifier.create(id).toString()] = BlockModel.fromJson(models[id])
		})
		Object.values(this.blockModels).forEach(m => m.flatten(this))
	}

	private createTextureAtlas(uvMapping: BrowserResourceAssets['uvMapping'], image: BrowserAtlasSource): TextureAtlas {
		const width = upperPowerOfTwo(image.width)
		const height = upperPowerOfTwo(image.height)
		const canvas = document.createElement('canvas')
		canvas.width = width
		canvas.height = height
		const ctx = canvas.getContext('2d')!
		if (image instanceof ImageData) {
			ctx.putImageData(image, 0, 0)
		} else {
			ctx.drawImage(image, 0, 0)
		}
		const atlasData = ctx.getImageData(0, 0, width, height)
		const idMap: Record<string, [number, number, number, number]> = {}
		Object.keys(uvMapping).forEach(id => {
			const [u, v, du, dv] = uvMapping[id]
			const dv2 = (du !== dv && id.startsWith('block/')) ? du : dv
			idMap[Identifier.create(id).toString()] = [u / width, v / height, (u + du) / width, (v + dv2) / height]
		})
		return new TextureAtlas(atlasData, idMap)
	}

	private getMipMapLevels(image: ImageData, gl: WebGL2RenderingContext, levels: number[]): Uint8Array[] {
		const tex = gl.createTexture()
		gl.bindTexture(gl.TEXTURE_2D, tex)
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, image.width, image.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, image.data)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_LINEAR)
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
		gl.generateMipmap(gl.TEXTURE_2D)

		const fbo = gl.createFramebuffer()
		gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
		const maxLevel = Math.floor(Math.log2(Math.max(image.width, image.height))) + 1
		const result: Uint8Array[] = []
		for (const level of levels) {
			if (level < 0 || level >= maxLevel) {
				result.push(new Uint8Array())
				continue
			}
			gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, level)
			if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
				result.push(new Uint8Array())
				continue
			}
			const w = Math.max(1, image.width >> level)
			const h = Math.max(1, image.height >> level)
			const pixels = new Uint8Array(w * h * 4)
			gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels)
			result.push(pixels)
		}
		gl.bindFramebuffer(gl.FRAMEBUFFER, null)
		gl.deleteFramebuffer(fbo)
		gl.deleteTexture(tex)
		return result
	}

	private getTextureIsOpaque(textureId: Identifier, imageData: ImageData, mipMapLevelFour: Uint8Array): boolean {
		if (mipMapLevelFour.length === 0) {
			return false
		}
		const uv = this.textureAtlas.getTextureUV(textureId)
		const width = imageData.width >> 4
		const height = imageData.height >> 4
		const u = Math.min(width - 1, Math.max(0, Math.floor(uv[0] * width)))
		const v = Math.min(height - 1, Math.max(0, Math.floor(uv[1] * height)))
		return mipMapLevelFour[(v * width + u) * 4 + 3] === 255
	}
}
